import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Clock, Play, Search, CheckCircle2, Car, User, Wrench,
  Loader2, RefreshCw, Calendar, ChevronRight, ClipboardCheck,
  Camera, AlertTriangle, Eye, Moon
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Card, CardContent } from "../ui/card";
import { AdjustServicesDialog } from "./AdjustServicesDialog";
import { API } from "../../App";
import { WashIncludedBadge } from "../WashPerk";

const STAGES = [
  { id: "confirmed", label: "Pending", icon: Clock, color: "slate", bgColor: "bg-slate-800/50" },
  { id: "checked_in", label: "Checked In", icon: ClipboardCheck, color: "blue", bgColor: "bg-blue-500/10" },
  { id: "in_progress", label: "In Progress", icon: Play, color: "yellow", bgColor: "bg-yellow-500/10" },
  { id: "qc", label: "Quality Check", icon: Search, color: "purple", bgColor: "bg-purple-500/10" },
  { id: "completed", label: "Completed", icon: CheckCircle2, color: "green", bgColor: "bg-green-500/10" }
];

const formatDuration = (mins) => {
  const m = Number(mins) || 0;
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r === 0 ? `${h}h` : `${h}h ${r}m`;
};

const bookingDuration = (b) => {
  // Prefer explicit total_duration (customer flow), then duration_minutes (staff/manual + completion),
  // then sum of services_details, else 60.
  const explicit = b.total_duration || b.duration_minutes;
  if (explicit) return explicit;
  const summed = (b.services_details || []).reduce((s, x) => s + (x.duration_minutes || 0), 0);
  return summed || 60;
};

const daysSince = (dateStr) => {
  if (!dateStr) return 0;
  const d = new Date(dateStr + "T00:00:00");
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((today - d) / (1000 * 60 * 60 * 24));
  return Math.max(0, diff);
};

const JobCard = ({ booking, onCheckIn, onStartWork, onSendToQC, onComplete, onViewCard, onAdjustServices, isAdmin }) => {
  const stage = STAGES.find(s => s.id === booking.status) || STAGES[0];
  const StageIcon = stage.icon;

  const getNextAction = () => {
    switch (booking.status) {
      case "confirmed":
        return { label: "Check In", action: () => onCheckIn(booking), color: "bg-blue-600 hover:bg-blue-700" };
      case "checked_in":
        return { label: "Start Work", action: () => onStartWork(booking), color: "bg-yellow-600 hover:bg-yellow-700" };
      case "in_progress":
        return { label: "Send to QC", action: () => onSendToQC(booking), color: "bg-purple-600 hover:bg-purple-700" };
      case "qc":
        return { label: "Complete", action: () => onComplete(booking), color: "bg-green-600 hover:bg-green-700" };
      default:
        return null;
    }
  };

  const nextAction = getNextAction();

  return (
    <Card className={`${stage.bgColor} border-slate-700 hover:border-slate-600 transition-all cursor-pointer group`}>
      <CardContent className="p-4 space-y-3">
        {/* Header with vehicle reg */}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <Car className={`w-5 h-5 flex-shrink-0 text-${stage.color}-400`} />
            <span className="font-mono font-bold text-lg text-saturn-orange truncate">
              {booking.vehicle_reg || booking.vehicle?.reg || "No Reg"}
            </span>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap justify-end">
            {booking.is_redo && (
              <Badge
                className="bg-amber-500/25 text-amber-200 border-amber-400/50 text-xs font-bold whitespace-nowrap"
                title={`Free return visit — ${booking.redo_area || "see note"}`}
                data-testid={`job-board-redo-${booking.booking_id}`}
              >
                <RefreshCw className="w-3 h-3 mr-1" />
                REDO
              </Badge>
            )}
            {booking.is_carry_over && (
              <Badge
                className="bg-orange-500/20 text-orange-300 border-orange-500/40 text-xs whitespace-nowrap"
                title={`Booked ${booking.booking_date} — still on-site`}
                data-testid={`job-board-carryover-${booking.booking_id}`}
              >
                <Moon className="w-3 h-3 mr-1" />
                {daysSince(booking.booking_date)}d on-site
              </Badge>
            )}
            <Badge className={`whitespace-nowrap bg-${stage.color}-500/20 text-${stage.color}-400 border-${stage.color}-500/30`}>
              <StageIcon className="w-3 h-3 mr-1" />
              {stage.label}
            </Badge>
          </div>
        </div>

        {/* Vehicle info */}
        <div className="text-sm text-slate-300">
          {booking.vehicle?.year} {booking.vehicle?.make} {booking.vehicle?.model}
        </div>

        {/* Customer */}
        <div className="flex items-center gap-2 text-sm">
          <User className="w-4 h-4 text-slate-500" />
          <span className="text-slate-400">{booking.customer_name}</span>
        </div>

        {/* Services */}
        <div className="flex items-center gap-2 text-sm">
          <Wrench className="w-4 h-4 text-slate-500" />
          <span className="text-slate-400 truncate">
            {booking.services_details?.map(s => s.name).join(", ") || "No services"}
          </span>
        </div>

        {/* Complimentary wash — the perk only, never its cost. */}
        {booking.wash_perk?.entitled && (
          <div data-testid={`job-board-wash-${booking.booking_id}`}>
            <WashIncludedBadge testId={`wash-included-badge-${booking.booking_id}`} />
          </div>
        )}

        {/* Time */}
        <div className="flex items-center gap-2 text-sm">
          <Clock className="w-4 h-4 text-slate-500" />
          <span className="text-white">{booking.booking_time}</span>
          <span className="text-slate-500">•</span>
          <span className="text-slate-400">{formatDuration(bookingDuration(booking))}</span>
        </div>

        {/* Job info badges */}
        <div className="flex flex-wrap gap-1">
          {booking.has_before_photos && (
            <Badge className="bg-blue-500/20 text-blue-400 text-xs">
              <Camera className="w-3 h-3 mr-1" />
              Photos
            </Badge>
          )}
          {booking.has_damage && (
            <Badge className="bg-yellow-500/20 text-yellow-400 text-xs">
              <AlertTriangle className="w-3 h-3 mr-1" />
              Damage
            </Badge>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-2 border-t border-slate-700">
          {nextAction && (
            <Button
              size="sm"
              className={`flex-1 ${nextAction.color}`}
              onClick={(e) => {
                e.stopPropagation();
                nextAction.action();
              }}
              data-testid={`job-board-action-${booking.booking_id}`}
            >
              {nextAction.label}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
          {["checked_in", "in_progress", "qc"].includes(booking.status) && (
            <Button
              size="sm"
              variant="outline"
              className="border-slate-600 text-slate-300 hover:bg-slate-700/50"
              onClick={(e) => {
                e.stopPropagation();
                onViewCard(booking);
              }}
              title="View job card"
              data-testid={`job-board-view-card-${booking.booking_id}`}
            >
              <Eye className="w-4 h-4" />
            </Button>
          )}
          {/* Scope changed at the door — fix services AND the balance
              together (Amir Ali, Oct 2026). Hidden once the money is
              closed, matching the endpoint's own guards. */}
          {isAdmin
            && ["confirmed", "checked_in", "in_progress"].includes(booking.status)
            && !booking.balance_paid && !booking.invoice_id && onAdjustServices && (
            <Button
              size="sm"
              variant="outline"
              className="border-saturn-orange/50 text-saturn-orange hover:bg-saturn-orange/10"
              onClick={(e) => {
                e.stopPropagation();
                onAdjustServices(booking);
              }}
              title="Change services / price"
              data-testid={`job-board-adjust-${booking.booking_id}`}
            >
              <Wrench className="w-4 h-4" />
            </Button>
          )}
          {booking.status === "completed" && (
            <Button
              size="sm"
              variant="outline"
              className="flex-1 border-green-500/30 text-green-400 hover:bg-green-500/10"
              onClick={(e) => {
                e.stopPropagation();
                onViewCard(booking);
              }}
              data-testid={`job-board-view-${booking.booking_id}`}
            >
              View Card
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const JobBoardSection = ({ 
  isAdmin,
  currentUserId,
  onCheckIn, 
  onStartWork, 
  onSendToQC, 
  onComplete, 
  onViewCard,
  refreshTrigger 
}) => {
  const [bookings, setBookings] = useState([]);
  // Upcoming redo visits assigned to whoever is looking at the board. Read
  // from the same bookings endpoint (no new endpoint, nothing admin-only):
  // the booking carries the area and the customer's note, never fault or
  // cost. Loaded across dates because the point is "coming up", not "today".
  const [myRedos, setMyRedos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [materialsBooking, setMaterialsBooking] = useState(null);
  const [adjustBooking, setAdjustBooking] = useState(null);

  const loadBookings = useCallback(async () => {
    setIsLoading(true);
    try {
      // Get all bookings from auth endpoint
      const response = await axios.get(`${API}/auth/bookings/all`);
      let allBookings = response.data || [];

      const today = new Date().toISOString().split('T')[0];
      const viewingToday = selectedDate === today;
      const activeStatuses = ["checked_in", "in_progress", "qc"];

      const horizon = new Date(Date.now() + 14 * 86400000).toISOString().split('T')[0];
      setMyRedos(
        allBookings.filter(b =>
          b.is_redo
          && b.booking_date >= today && b.booking_date <= horizon
          && !["cancelled", "completed", "expired"].includes(b.status)
          && (!currentUserId || b.assigned_technician_id === currentUserId)
        ).sort((a, b) => (a.booking_date + a.booking_time).localeCompare(
          b.booking_date + b.booking_time))
      );

      // Filter for selected date, plus carry-over cars on-site when viewing today.
      // A car checked in yesterday but still in progress must remain visible.
      let dayBookings;
      if (viewingToday) {
        dayBookings = allBookings.filter(b =>
          b.booking_date === selectedDate ||
          (activeStatuses.includes(b.status) && b.booking_date <= selectedDate)
        );
      } else {
        dayBookings = allBookings.filter(b => b.booking_date === selectedDate);
      }

      // De-dupe just in case
      const seen = new Set();
      dayBookings = dayBookings.filter(b => {
        if (seen.has(b.booking_id)) return false;
        seen.add(b.booking_id);
        return true;
      });

      // Enrich with job stage info
      const enrichedBookings = await Promise.all(
        dayBookings.map(async (booking) => {
          try {
            const stageResponse = await axios.get(`${API}/jobs/stage/${booking.booking_id}`);
            const currentStatus = stageResponse.data.checked_in ? stageResponse.data.stage : booking.status;
            return {
              ...booking,
              has_before_photos: stageResponse.data.before_photos_count > 0,
              has_damage: stageResponse.data.damage_records_count > 0,
              status: currentStatus,
              is_carry_over: viewingToday && booking.booking_date !== today && activeStatuses.includes(currentStatus),
            };
          } catch {
            return {
              ...booking,
              is_carry_over: viewingToday && booking.booking_date !== today && activeStatuses.includes(booking.status),
            };
          }
        })
      );

      setBookings(enrichedBookings);
    } catch (error) {
      console.error("Failed to load bookings:", error);
      toast.error("Failed to load job board");
    } finally {
      setIsLoading(false);
    }
  }, [selectedDate, currentUserId]);

  useEffect(() => {
    loadBookings();
  }, [loadBookings, refreshTrigger]);

  // Handle stage updates
  const handleStageUpdate = async (bookingId, newStage) => {
    try {
      await axios.post(`${API}/jobs/stage/${bookingId}?new_stage=${newStage}`);
      toast.success(`Job moved to ${STAGES.find(s => s.id === newStage)?.label}`);
      loadBookings(); // Refresh
    } catch (error) {
      console.error("Failed to update stage:", error);
      toast.error(error.response?.data?.detail || "Failed to update job stage");
    }
  };

  // Group bookings by stage
  const bookingsByStage = STAGES.reduce((acc, stage) => {
    acc[stage.id] = bookings.filter(b => b.status === stage.id);
    return acc;
  }, {});

  const totalJobs = bookings.length;
  const activeJobs = bookings.filter(b =>
    ["checked_in", "in_progress", "qc"].includes(b.status)
  ).length;

  // Overnight/carry-over cars on-site (from a previous day, still active)
  const overnightJobs = bookings.filter(b => b.is_carry_over);
  const maxDaysOnSite = overnightJobs.reduce(
    (max, b) => Math.max(max, daysSince(b.booking_date)),
    0
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-saturn-orange" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Calendar className="w-6 h-6 text-saturn-orange" />
            Job Board
          </h2>
          <p className="text-slate-400 text-sm mt-1">
            {totalJobs} jobs scheduled • {activeJobs} in progress
          </p>
        </div>
        <div className="flex items-center gap-3">
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-white text-sm"
            data-testid="job-board-date"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={loadBookings}
            className="border-slate-600"
            data-testid="job-board-refresh"
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Redo visits coming up for whoever is looking at the board. */}
      {myRedos.length > 0 && (
        <div
          className="rounded-lg border border-amber-400/50 bg-gradient-to-r from-amber-500/20 to-slate-800/40 p-3 sm:p-4 flex items-start sm:items-center gap-3 flex-wrap"
          data-testid="redo-upcoming-banner"
        >
          <div className="w-9 h-9 rounded-lg bg-amber-500/25 flex items-center justify-center flex-shrink-0">
            <RefreshCw className="w-5 h-5 text-amber-200" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white font-medium leading-tight">
              {myRedos.length === 1
                ? "1 redo visit coming up for you"
                : `${myRedos.length} redo visits coming up for you`}
              <span className="text-amber-200"> · free return visit, no payment to take</span>
            </p>
            <div className="flex flex-col gap-1 mt-1.5">
              {myRedos.slice(0, 4).map(b => (
                <span key={b.booking_id} className="text-xs text-amber-100/90"
                      data-testid={`redo-banner-row-${b.booking_id}`}>
                  <span className="font-mono font-semibold">
                    {b.vehicle_reg || b.vehicle?.reg || b.booking_id.slice(-6)}
                  </span>
                  {" · "}{b.booking_date} {b.booking_time}
                  {b.redo_area ? ` · ${b.redo_area}` : ""}
                  {b.redo_customer_note ? ` · “${b.redo_customer_note}”` : ""}
                </span>
              ))}
              {myRedos.length > 4 && (
                <span className="text-xs text-slate-300">+{myRedos.length - 4} more</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Overnight / carry-over on-site cars — always visible when there are any */}
      {overnightJobs.length > 0 && (
        <div
          className="rounded-lg border border-orange-500/40 bg-gradient-to-r from-orange-500/15 to-slate-800/40 p-3 sm:p-4 flex items-start sm:items-center gap-3 flex-wrap"
          data-testid="overnight-jobs-summary"
        >
          <div className="w-9 h-9 rounded-lg bg-orange-500/25 flex items-center justify-center flex-shrink-0">
            <Moon className="w-5 h-5 text-orange-300" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white font-medium leading-tight">
              {overnightJobs.length === 1
                ? "1 vehicle stayed overnight — still on-site"
                : `${overnightJobs.length} vehicles are still on-site from previous days`}
              {maxDaysOnSite > 1 && (
                <span className="text-orange-300"> · oldest {maxDaysOnSite} days</span>
              )}
            </p>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {overnightJobs.slice(0, 6).map(b => (
                <Badge
                  key={b.booking_id}
                  className="bg-orange-500/20 text-orange-200 border-orange-500/40 text-xs font-mono"
                  title={`${b.vehicle?.make || ""} ${b.vehicle?.model || ""} · booked ${b.booking_date} · ${b.status}`}
                >
                  {b.vehicle_reg || b.vehicle?.reg || b.booking_id.slice(-6)}
                  <span className="ml-1 text-orange-300/70">
                    ({daysSince(b.booking_date)}d)
                  </span>
                </Badge>
              ))}
              {overnightJobs.length > 6 && (
                <Badge className="bg-slate-700 text-slate-300 text-xs">+{overnightJobs.length - 6} more</Badge>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Stage Columns - horizontal scroll on smaller screens, full board on xl */}
      <div className="flex gap-4 overflow-x-auto pb-3 xl:grid xl:grid-cols-5 xl:overflow-visible">
        {STAGES.map((stage) => {
          const stageBookings = bookingsByStage[stage.id] || [];
          const StageIcon = stage.icon;
          
          return (
            <div key={stage.id} className="space-y-3 w-72 flex-shrink-0 xl:w-auto">
              {/* Column Header */}
              <div className={`${stage.bgColor} rounded-lg p-3 border border-slate-700`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <StageIcon className={`w-5 h-5 text-${stage.color}-400`} />
                    <span className="font-medium text-white">{stage.label}</span>
                  </div>
                  <Badge className={`bg-${stage.color}-500/30 text-${stage.color}-300`}>
                    {stageBookings.length}
                  </Badge>
                </div>
              </div>

              {/* Job Cards */}
              <div className="space-y-3 min-h-[200px]">
                {stageBookings.length === 0 ? (
                  <div className="text-center py-8 text-slate-500 text-sm">
                    No jobs
                  </div>
                ) : (
                  stageBookings.map((booking) => (
                    <JobCard
                      key={booking.booking_id}
                      booking={booking}
                      onCheckIn={onCheckIn}
                      onStartWork={(b) => handleStageUpdate(b.booking_id, "in_progress")}
                      onSendToQC={(b) => handleStageUpdate(b.booking_id, "qc")}
                      onComplete={onComplete}
                      onViewCard={onViewCard}
                      onAdjustServices={setAdjustBooking}
                      isAdmin={isAdmin}
                    />
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {totalJobs === 0 && (
        <div className="text-center py-12 bg-slate-800/30 rounded-lg border border-slate-700">
          <Calendar className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400">No jobs scheduled for {selectedDate}</p>
          <p className="text-slate-500 text-sm mt-1">
            Select a different date or create new bookings
          </p>
        </div>
      )}

      <AdjustServicesDialog
        booking={adjustBooking}
        open={isAdmin && !!adjustBooking}
        onClose={() => setAdjustBooking(null)}
        onSaved={() => { setAdjustBooking(null); loadBookings(); }}
      />
    </div>
  );
};

export default JobBoardSection;
